import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Shield,
  Settings,
  Check,
  X,
  AlertCircle,
  Info,
  Copy,
  Download,
  RefreshCw,
  Key,
  Globe,
  Lock,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Link as LinkIcon
} from 'lucide-react';

const EnterpriseSSOConfiguration = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);

  // Feature authorization - check if SAML and OIDC are enabled in the package
  const [samlAuthorized, setSamlAuthorized] = useState(false);
  const [oidcAuthorized, setOidcAuthorized] = useState(false);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  // SSO type selection: 'none', 'saml', 'oidc'
  const [ssoType, setSsoType] = useState('none');
  const [originalSsoType, setOriginalSsoType] = useState('none');

  // Existing configs from server
  const [samlConfig, setSamlConfig] = useState(null);
  const [oidcConfig, setOidcConfig] = useState(null);

  // SAML form state
  const [samlFormData, setSamlFormData] = useState({
    idp_entity_id: '',
    idp_sso_url: '',
    idp_slo_url: '',
    idp_x509_cert: '',
    saml_domains: '',
    want_assertions_signed: true,
    want_response_signed: true,
    authn_requests_signed: true,
    allow_unencrypted_assertion: true,
    force_authn: false,
    is_enabled: false
  });

  // OIDC form state
  const [oidcFormData, setOidcFormData] = useState({
    provider_type: 'generic',
    client_id: '',
    client_secret: '',
    issuer_url: '',
    scopes: 'openid, email, profile',
    use_pkce: true,
    require_email_verified: false,
    oidc_domains: '',
    is_enabled: false
  });

  // Shared authentication enforcement settings
  const [authEnforcement, setAuthEnforcement] = useState({
    allow_google_sso: false,
    allow_microsoft_sso: false,
    allow_password_login: false,
    allow_passkey_login: false
  });

  useEffect(() => {
    loadFeatureAuthorization();
    loadConfigs();
  }, []);

  const loadFeatureAuthorization = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Check both SAML and OIDC feature authorization in parallel
      const [samlResponse, oidcResponse] = await Promise.all([
        axios.get(`${apiBase}/organizations/feature-check/saml`, { headers }).catch(() => ({ data: { enabled: false } })),
        axios.get(`${apiBase}/organizations/feature-check/oidc`, { headers }).catch(() => ({ data: { enabled: false } }))
      ]);

      setSamlAuthorized(samlResponse.data.enabled);
      setOidcAuthorized(oidcResponse.data.enabled);
    } catch (err) {
      console.error('Failed to check feature authorization:', err);
      // Default to false if check fails
      setSamlAuthorized(false);
      setOidcAuthorized(false);
    } finally {
      setFeaturesLoaded(true);
    }
  };

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Load both configs in parallel
      const [samlResponse, oidcResponse] = await Promise.all([
        axios.get(`${apiBase}/saml/config`, { headers }).catch(() => ({ data: { configured: false } })),
        axios.get(`${apiBase}/oidc/config`, { headers }).catch(() => ({ data: { configured: false } }))
      ]);

      // Determine which SSO type is active
      let activeSsoType = 'none';
      let activeAuthEnforcement = {
        allow_google_sso: false,
        allow_microsoft_sso: false,
        allow_password_login: false,
        allow_passkey_login: false
      };

      if (samlResponse.data.configured) {
        setSamlConfig(samlResponse.data);
        setSamlFormData({
          idp_entity_id: samlResponse.data.idp_entity_id || '',
          idp_sso_url: samlResponse.data.idp_sso_url || '',
          idp_slo_url: samlResponse.data.idp_slo_url || '',
          idp_x509_cert: '',
          saml_domains: (samlResponse.data.saml_domains || []).join(', '),
          want_assertions_signed: samlResponse.data.want_assertions_signed ?? true,
          want_response_signed: samlResponse.data.want_response_signed ?? true,
          authn_requests_signed: samlResponse.data.authn_requests_signed ?? true,
          allow_unencrypted_assertion: samlResponse.data.allow_unencrypted_assertion ?? true,
          force_authn: samlResponse.data.force_authn ?? false,
          is_enabled: samlResponse.data.is_enabled ?? false
        });

        if (samlResponse.data.is_enabled) {
          activeSsoType = 'saml';
          activeAuthEnforcement = {
            allow_google_sso: samlResponse.data.allow_google_sso ?? false,
            allow_microsoft_sso: samlResponse.data.allow_microsoft_sso ?? false,
            allow_password_login: samlResponse.data.allow_password_login ?? false,
            allow_passkey_login: samlResponse.data.allow_passkey_login ?? false
          };
        }
      }

      if (oidcResponse.data.configured) {
        setOidcConfig(oidcResponse.data);
        setOidcFormData({
          provider_type: oidcResponse.data.provider_type || 'generic',
          client_id: oidcResponse.data.client_id || '',
          client_secret: '',
          issuer_url: oidcResponse.data.issuer_url || '',
          scopes: (oidcResponse.data.scopes || ['openid', 'email', 'profile']).join(', '),
          use_pkce: oidcResponse.data.use_pkce ?? true,
          require_email_verified: oidcResponse.data.require_email_verified ?? false,
          oidc_domains: (oidcResponse.data.oidc_domains || []).join(', '),
          is_enabled: oidcResponse.data.is_enabled ?? false
        });

        if (oidcResponse.data.is_enabled) {
          activeSsoType = 'oidc';
          activeAuthEnforcement = {
            allow_google_sso: oidcResponse.data.allow_google_sso ?? false,
            allow_microsoft_sso: oidcResponse.data.allow_microsoft_sso ?? false,
            allow_password_login: oidcResponse.data.allow_password_login ?? false,
            allow_passkey_login: oidcResponse.data.allow_passkey_login ?? false
          };
        }
      }

      setSsoType(activeSsoType);
      setOriginalSsoType(activeSsoType);
      setAuthEnforcement(activeAuthEnforcement);

    } catch (err) {
      setError('Failed to load configuration: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSamlInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSamlFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOidcInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOidcFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAuthEnforcementChange = (e) => {
    const { name, checked } = e.target;
    setAuthEnforcement(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSsoTypeChange = (newType) => {
    setSsoType(newType);
    setTestResult(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // If switching from one SSO type to another, disable the old one first
      if (originalSsoType !== ssoType && originalSsoType !== 'none') {
        if (originalSsoType === 'saml' && samlConfig) {
          await axios.put(`${apiBase}/saml/config`, { is_enabled: false }, { headers });
        } else if (originalSsoType === 'oidc' && oidcConfig) {
          await axios.put(`${apiBase}/oidc/config`, { is_enabled: false }, { headers });
        }
      }

      if (ssoType === 'none') {
        // Disable both if configured
        if (samlConfig) {
          await axios.put(`${apiBase}/saml/config`, { is_enabled: false }, { headers });
        }
        if (oidcConfig) {
          await axios.put(`${apiBase}/oidc/config`, { is_enabled: false }, { headers });
        }
        setSuccess('Enterprise SSO has been disabled');
      } else if (ssoType === 'saml') {
        const payload = {
          idp_entity_id: samlFormData.idp_entity_id,
          idp_sso_url: samlFormData.idp_sso_url,
          idp_slo_url: samlFormData.idp_slo_url || null,
          want_assertions_signed: samlFormData.want_assertions_signed,
          want_response_signed: samlFormData.want_response_signed,
          authn_requests_signed: samlFormData.authn_requests_signed,
          allow_unencrypted_assertion: samlFormData.allow_unencrypted_assertion,
          force_authn: samlFormData.force_authn,
          allow_google_sso: authEnforcement.allow_google_sso,
          allow_microsoft_sso: authEnforcement.allow_microsoft_sso,
          allow_password_login: authEnforcement.allow_password_login,
          allow_passkey_login: authEnforcement.allow_passkey_login,
          saml_domains: samlFormData.saml_domains.split(',').map(d => d.trim()).filter(d => d),
          is_enabled: samlFormData.is_enabled
        };

        if (samlFormData.idp_x509_cert) {
          payload.idp_x509_cert = samlFormData.idp_x509_cert;
        }

        if (samlConfig) {
          await axios.put(`${apiBase}/saml/config`, payload, { headers });
          setSuccess('SAML 2.0 configuration updated successfully');
        } else {
          if (!samlFormData.idp_x509_cert) {
            setError('IdP X.509 certificate is required for new SAML configuration');
            setSaving(false);
            return;
          }
          await axios.post(`${apiBase}/saml/config`, payload, { headers });
          setSuccess('SAML 2.0 configuration created successfully');
        }
      } else if (ssoType === 'oidc') {
        const payload = {
          provider_type: oidcFormData.provider_type,
          client_id: oidcFormData.client_id,
          issuer_url: oidcFormData.issuer_url,
          scopes: oidcFormData.scopes.split(',').map(s => s.trim()).filter(s => s),
          use_pkce: oidcFormData.use_pkce,
          require_email_verified: oidcFormData.require_email_verified,
          allow_google_sso: authEnforcement.allow_google_sso,
          allow_microsoft_sso: authEnforcement.allow_microsoft_sso,
          allow_password_login: authEnforcement.allow_password_login,
          allow_passkey_login: authEnforcement.allow_passkey_login,
          oidc_domains: oidcFormData.oidc_domains.split(',').map(d => d.trim()).filter(d => d),
          is_enabled: oidcFormData.is_enabled
        };

        if (oidcFormData.client_secret) {
          payload.client_secret = oidcFormData.client_secret;
        }

        if (oidcConfig) {
          await axios.put(`${apiBase}/oidc/config`, payload, { headers });
          setSuccess('OIDC configuration updated successfully');
        } else {
          if (!oidcFormData.client_secret) {
            setError('Client Secret is required for new OIDC configuration');
            setSaving(false);
            return;
          }
          await axios.post(`${apiBase}/oidc/config`, payload, { headers });
          setSuccess('OIDC configuration created successfully');
        }
      }

      await loadConfigs();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (ssoType === 'saml') {
        const response = await axios.get(`${apiBase}/saml/test`, { headers });
        setTestResult(response.data);
      } else if (ssoType === 'oidc') {
        const response = await axios.get(`${apiBase}/oidc/test`, { headers });
        setTestResult(response.data);
      }
    } catch (err) {
      setError('Test failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    const typeName = ssoType === 'saml' ? 'SAML 2.0' : 'OIDC';
    if (!window.confirm(`Are you sure you want to delete the ${typeName} configuration? Users will no longer be able to login via ${typeName} SSO.`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (ssoType === 'saml') {
        await axios.delete(`${apiBase}/saml/config`, { headers });
        setSamlConfig(null);
        setSamlFormData({
          idp_entity_id: '',
          idp_sso_url: '',
          idp_slo_url: '',
          idp_x509_cert: '',
          saml_domains: '',
          want_assertions_signed: true,
          want_response_signed: true,
          authn_requests_signed: true,
          allow_unencrypted_assertion: true,
          force_authn: false,
          is_enabled: false
        });
      } else if (ssoType === 'oidc') {
        await axios.delete(`${apiBase}/oidc/config`, { headers });
        setOidcConfig(null);
        setOidcFormData({
          provider_type: 'generic',
          client_id: '',
          client_secret: '',
          issuer_url: '',
          scopes: 'openid, email, profile',
          use_pkce: true,
          require_email_verified: false,
          oidc_domains: '',
          is_enabled: false
        });
      }

      setSsoType('none');
      setOriginalSsoType('none');
      setSuccess(`${typeName} configuration deleted`);
    } catch (err) {
      setError('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  const downloadMetadata = () => {
    if (samlConfig?.organization_id) {
      const apiBase = getApiBase();
      window.open(`${apiBase}/saml/metadata/${samlConfig.organization_id}`, '_blank');
    }
  };

  const currentConfig = ssoType === 'saml' ? samlConfig : ssoType === 'oidc' ? oidcConfig : null;
  const isEnabled = ssoType === 'saml' ? samlFormData.is_enabled : ssoType === 'oidc' ? oidcFormData.is_enabled : false;

  if (loading || !featuresLoaded) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
        <span className="ml-2">Loading SSO configuration...</span>
      </div>
    );
  }

  // Check if neither SAML nor OIDC is authorized
  if (!samlAuthorized && !oidcAuthorized) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-600" />
            Enterprise SSO Configuration
          </h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-2">Feature Not Available</h3>
              <p className="text-yellow-700">
                Enterprise SSO (SAML 2.0 and OIDC) is not included in your current package.
                Please contact your administrator or upgrade your plan to enable this feature.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-purple-600" />
          Enterprise SSO Configuration
        </h1>
        <p className="text-gray-600 mt-1">
          Configure single sign-on with your identity provider (Okta, Azure AD, OneLogin, etc.)
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SSO Type Selection */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-purple-600" />
            SSO Protocol
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Select which SSO protocol to use. Only one can be active at a time.
          </p>

          <div className={`grid grid-cols-1 gap-4 ${
            samlAuthorized && oidcAuthorized ? 'md:grid-cols-3' : 'md:grid-cols-2'
          }`}>
            <button
              type="button"
              onClick={() => handleSsoTypeChange('none')}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                ssoType === 'none'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <X className={`w-5 h-5 ${ssoType === 'none' ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className={`font-medium ${ssoType === 'none' ? 'text-purple-900' : 'text-gray-700'}`}>
                  Disabled
                </span>
              </div>
              <p className="text-xs text-gray-500">
                No enterprise SSO. Users authenticate with standard methods.
              </p>
            </button>

            {samlAuthorized && (
              <button
                type="button"
                onClick={() => handleSsoTypeChange('saml')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  ssoType === 'saml'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Shield className={`w-5 h-5 ${ssoType === 'saml' ? 'text-purple-600' : 'text-gray-400'}`} />
                  <span className={`font-medium ${ssoType === 'saml' ? 'text-purple-900' : 'text-gray-700'}`}>
                    SAML 2.0
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Security Assertion Markup Language. Widely supported by enterprise IdPs.
                </p>
              </button>
            )}

            {oidcAuthorized && (
              <button
                type="button"
                onClick={() => handleSsoTypeChange('oidc')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  ssoType === 'oidc'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <LinkIcon className={`w-5 h-5 ${ssoType === 'oidc' ? 'text-purple-600' : 'text-gray-400'}`} />
                  <span className={`font-medium ${ssoType === 'oidc' ? 'text-purple-900' : 'text-gray-700'}`}>
                    OIDC
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  OpenID Connect. Modern protocol built on OAuth 2.0. Recommended for Okta.
                </p>
              </button>
            )}
          </div>
        </div>

        {/* SAML Configuration */}
        {ssoType === 'saml' && (
          <>
            {/* Service Provider Info */}
            {samlConfig && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-3">
                  <Info className="w-5 h-5" />
                  Service Provider (SP) Information
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  Provide these values to your Identity Provider administrator:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <div>
                      <span className="text-xs text-gray-500">SP Entity ID / Audience URI</span>
                      <p className="font-mono text-sm">{samlConfig.sp_entity_id}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(samlConfig.sp_entity_id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <div>
                      <span className="text-xs text-gray-500">ACS URL (Assertion Consumer Service)</span>
                      <p className="font-mono text-sm">{samlConfig.sp_acs_url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(samlConfig.sp_acs_url)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={downloadMetadata}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4" />
                      Download SP Metadata XML
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* IdP Configuration */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-purple-600" />
                Identity Provider (IdP) Settings
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IdP Entity ID *
                  </label>
                  <input
                    type="text"
                    name="idp_entity_id"
                    value={samlFormData.idp_entity_id}
                    onChange={handleSamlInputChange}
                    placeholder="https://your-idp.com/metadata"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={ssoType === 'saml'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IdP Single Sign-On URL *
                  </label>
                  <input
                    type="url"
                    name="idp_sso_url"
                    value={samlFormData.idp_sso_url}
                    onChange={handleSamlInputChange}
                    placeholder="https://your-idp.com/sso/saml"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={ssoType === 'saml'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IdP Single Logout URL (Optional)
                  </label>
                  <input
                    type="url"
                    name="idp_slo_url"
                    value={samlFormData.idp_slo_url}
                    onChange={handleSamlInputChange}
                    placeholder="https://your-idp.com/slo/saml"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IdP X.509 Certificate {!samlConfig && '*'}
                  </label>
                  <textarea
                    name="idp_x509_cert"
                    value={samlFormData.idp_x509_cert}
                    onChange={handleSamlInputChange}
                    placeholder={samlConfig ? "(Certificate configured - paste new one to update)" : "-----BEGIN CERTIFICATE-----\nMIIC...\n-----END CERTIFICATE-----"}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                    required={ssoType === 'saml' && !samlConfig}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SAML Domains
                  </label>
                  <input
                    type="text"
                    name="saml_domains"
                    value={samlFormData.saml_domains}
                    onChange={handleSamlInputChange}
                    placeholder="example.com, company.org"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email domains that will use SAML SSO (comma-separated)
                  </p>
                </div>
              </div>
            </div>

            {/* SAML Security Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-purple-600" />
                SAML Security Settings
              </h3>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="want_assertions_signed"
                    checked={samlFormData.want_assertions_signed}
                    onChange={handleSamlInputChange}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Require signed assertions</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="want_response_signed"
                    checked={samlFormData.want_response_signed}
                    onChange={handleSamlInputChange}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Require signed SAML response</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="force_authn"
                    checked={samlFormData.force_authn}
                    onChange={handleSamlInputChange}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Force re-authentication on each login</span>
                </label>
              </div>
            </div>
          </>
        )}

        {/* OIDC Configuration */}
        {ssoType === 'oidc' && (
          <>
            {/* Redirect URI Info */}
            {oidcConfig && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-3">
                  <Info className="w-5 h-5" />
                  Redirect URI for Identity Provider
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  Configure this redirect URI in your identity provider:
                </p>
                <div className="flex items-center justify-between bg-white rounded p-2">
                  <div>
                    <span className="text-xs text-gray-500">Redirect URI / Callback URL</span>
                    <p className="font-mono text-sm">{oidcConfig.redirect_uri}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(oidcConfig.redirect_uri)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            )}

            {/* OIDC Provider Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-purple-600" />
                Identity Provider Settings
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider Type
                  </label>
                  <select
                    name="provider_type"
                    value={oidcFormData.provider_type}
                    onChange={handleOidcInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="okta">Okta</option>
                    <option value="azure_ad">Azure AD (Microsoft Entra ID)</option>
                    <option value="auth0">Auth0</option>
                    <option value="generic">Generic OIDC Provider</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issuer URL *
                  </label>
                  <input
                    type="url"
                    name="issuer_url"
                    value={oidcFormData.issuer_url}
                    onChange={handleOidcInputChange}
                    placeholder={
                      oidcFormData.provider_type === 'okta'
                        ? 'https://your-org.okta.com/oauth2/default'
                        : oidcFormData.provider_type === 'azure_ad'
                        ? 'https://login.microsoftonline.com/{tenant-id}/v2.0'
                        : 'https://your-idp.com'
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={ssoType === 'oidc'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID *
                  </label>
                  <input
                    type="text"
                    name="client_id"
                    value={oidcFormData.client_id}
                    onChange={handleOidcInputChange}
                    placeholder="Your OIDC client/application ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={ssoType === 'oidc'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret {!oidcConfig && '*'}
                  </label>
                  <input
                    type="password"
                    name="client_secret"
                    value={oidcFormData.client_secret}
                    onChange={handleOidcInputChange}
                    placeholder={oidcConfig ? "(Secret configured - enter new one to update)" : "Your OIDC client secret"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={ssoType === 'oidc' && !oidcConfig}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OIDC Domains
                  </label>
                  <input
                    type="text"
                    name="oidc_domains"
                    value={oidcFormData.oidc_domains}
                    onChange={handleOidcInputChange}
                    placeholder="example.com, company.org"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email domains that will use OIDC SSO (comma-separated)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scopes
                  </label>
                  <input
                    type="text"
                    name="scopes"
                    value={oidcFormData.scopes}
                    onChange={handleOidcInputChange}
                    placeholder="openid, email, profile"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="use_pkce"
                    checked={oidcFormData.use_pkce}
                    onChange={handleOidcInputChange}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Use PKCE (Recommended)</span>
                    <p className="text-xs text-gray-500">Proof Key for Code Exchange for enhanced security</p>
                  </div>
                </label>
              </div>
            </div>
          </>
        )}

        {/* Test Results */}
        {testResult && (
          <div className={`p-4 rounded-lg border ${testResult.valid ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              {testResult.valid ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-900">Configuration Valid</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-900">Configuration Issues Found</span>
                </>
              )}
            </h3>
            {testResult.discovery_success && (
              <p className="text-sm text-green-700 mb-2">OIDC discovery successful</p>
            )}
            {testResult.errors?.length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-medium text-red-700">Errors:</p>
                <ul className="list-disc list-inside text-sm text-red-600">
                  {testResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            {testResult.warnings?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-yellow-700">Warnings:</p>
                <ul className="list-disc list-inside text-sm text-yellow-600">
                  {testResult.warnings.map((warn, i) => <li key={i}>{warn}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Authentication Enforcement (shared) */}
        {ssoType !== 'none' && (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Alternative Authentication Methods
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Control which authentication methods are allowed in addition to {ssoType === 'saml' ? 'SAML 2.0' : 'OIDC'} SSO.
              By default, all are disabled to enforce SSO-only authentication.
            </p>

            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="allow_google_sso"
                  checked={authEnforcement.allow_google_sso}
                  onChange={handleAuthEnforcementChange}
                  className="w-4 h-4 text-purple-600 rounded mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Allow Google SSO</span>
                  <p className="text-xs text-gray-500">Users can sign in with their Google account</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="allow_microsoft_sso"
                  checked={authEnforcement.allow_microsoft_sso}
                  onChange={handleAuthEnforcementChange}
                  className="w-4 h-4 text-purple-600 rounded mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Allow Microsoft SSO</span>
                  <p className="text-xs text-gray-500">Users can sign in with their Microsoft account</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="allow_password_login"
                  checked={authEnforcement.allow_password_login}
                  onChange={handleAuthEnforcementChange}
                  className="w-4 h-4 text-purple-600 rounded mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Allow Password Login</span>
                  <p className="text-xs text-gray-500">Users can sign in with email and password</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <input
                  type="checkbox"
                  name="allow_passkey_login"
                  checked={authEnforcement.allow_passkey_login}
                  onChange={handleAuthEnforcementChange}
                  className="w-4 h-4 text-purple-600 rounded mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Allow Passkey Login</span>
                  <p className="text-xs text-gray-500">Users can sign in with their registered passkey</p>
                </div>
              </label>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Security Note:</strong> Keeping all options disabled enforces strict SSO authentication.
                  Enable alternatives only if required by your organization's security policy.
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        {ssoType !== 'none' && currentConfig && (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-purple-600" />
              Status
            </h3>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => {
                  if (ssoType === 'saml') {
                    setSamlFormData(prev => ({ ...prev, is_enabled: e.target.checked }));
                  } else if (ssoType === 'oidc') {
                    setOidcFormData(prev => ({ ...prev, is_enabled: e.target.checked }));
                  }
                }}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Enable {ssoType === 'saml' ? 'SAML 2.0' : 'OIDC'} SSO
                </span>
                <p className="text-sm text-gray-500">
                  When enabled, users with matching email domains can log in via SSO
                </p>
              </div>
            </label>

            {currentConfig.last_login_at && (
              <p className="mt-3 text-sm text-gray-500">
                Last SSO login: {new Date(currentConfig.last_login_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex gap-2">
            {currentConfig && ssoType !== 'none' && (
              <>
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing}
                  className="flex items-center gap-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  Test Configuration
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Save Configuration
          </button>
        </div>
      </form>

      {/* Help Section */}
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Info className="w-5 h-5 text-gray-600" />
          Setup Guide
        </h3>
        {ssoType === 'saml' ? (
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>1.</strong> Get IdP metadata from your identity provider (Okta, Azure AD, OneLogin, etc.)</p>
            <p><strong>2.</strong> Enter the IdP Entity ID, SSO URL, and X.509 certificate above</p>
            <p><strong>3.</strong> Save the configuration and download the SP Metadata XML</p>
            <p><strong>4.</strong> Import the SP Metadata into your identity provider</p>
            <p><strong>5.</strong> Configure attribute mapping in your IdP to send email, first name, and last name</p>
            <p><strong>6.</strong> Test the configuration, then enable SAML SSO</p>
          </div>
        ) : ssoType === 'oidc' ? (
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>1.</strong> Create an OIDC application in your identity provider</p>
            <p><strong>2.</strong> Set the application type to "Web Application" with "Authorization Code" grant</p>
            <p><strong>3.</strong> Copy the Client ID and Client Secret from your IdP</p>
            <p><strong>4.</strong> Enter the Issuer URL (e.g., https://your-org.okta.com/oauth2/default)</p>
            <p><strong>5.</strong> Save the configuration and copy the Redirect URI shown above</p>
            <p><strong>6.</strong> Add the Redirect URI to your IdP's allowed callback URLs</p>
            <p><strong>7.</strong> Test the configuration, then enable OIDC SSO</p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            Select an SSO protocol above to see the setup instructions.
          </p>
        )}
      </div>
    </div>
  );
};

export default EnterpriseSSOConfiguration;
